import { useEffect, useState } from 'react';
import {
  IconSearch, IconAlertCircle, IconChevronLeft, IconChevronRight,
  IconShieldHalf,
} from '@tabler/icons-react';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';

interface AuditEvent {
  id: string;
  ts: string;
  action: string;
  actorRole: string | null;
  actor: { id: string; name: string } | null;
  targetType: string | null;
  targetId: string | null;
  before: unknown;
  after: unknown;
  reason: string | null;
  ip: string | null;
  userAgent: string | null;
  stationId: string | null;
}

const ACTION_TONE: Record<string, string> = {
  // auth — info
  'auth.login.pin':      'bg-info-soft text-info',
  'auth.login.password': 'bg-info-soft text-info',
  'auth.login.failed':   'bg-danger-soft text-danger',
  'auth.elevate':        'bg-warning-soft text-warning',
  'auth.elevate.failed': 'bg-danger-soft text-danger',
  // money-moving — primary/warning
  'order.placed':                  'bg-success-soft text-success',
  'order.cancel':                  'bg-warning-soft text-warning',
  'order.void':                    'bg-danger-soft text-danger',
  'order.refund':                  'bg-danger-soft text-danger',
  'line.void':                     'bg-warning-soft text-warning',
  'discount.apply':                'bg-primary-soft text-primary',
  'drawer.paid_in':                'bg-success-soft text-success',
  'drawer.paid_out':               'bg-warning-soft text-warning',
  // day session
  'day_session.open':              'bg-success-soft text-success',
  'day_session.close':             'bg-info-soft text-info',
  'day_session.close_with_variance': 'bg-warning-soft text-warning',
  'day_session.corrected':         'bg-warning-soft text-warning',
  // stock
  'stock.adjust':                  'bg-primary-soft text-primary',
  'stock.wastage':                 'bg-danger-soft text-danger',
  'stock_take.submit':             'bg-info-soft text-info',
  'grv.receive':                   'bg-success-soft text-success',
  // user / account
  'user.create':                   'bg-success-soft text-success',
  'user.update':                   'bg-primary-soft text-primary',
  'user.deactivate':               'bg-danger-soft text-danger',
  'user.activate':                 'bg-success-soft text-success',
  'user.pin_change':               'bg-warning-soft text-warning',
  'user.password_change':          'bg-warning-soft text-warning',
  'account.create':                'bg-success-soft text-success',
  'account.suspend':               'bg-warning-soft text-warning',
  'account.credit_limit.update':   'bg-warning-soft text-warning',
  'account.payment_received':      'bg-success-soft text-success',
  'settings.update':               'bg-primary-soft text-primary',
};

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-ZA', { dateStyle: 'short', timeStyle: 'medium', timeZone: 'Africa/Johannesburg' });
}

export default function AdminAuditPage() {
  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [actions, setActions] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [openEvent, setOpenEvent] = useState<AuditEvent | null>(null);

  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (actionFilter) params.set('action', actionFilter);
      if (fromDate) params.set('from', new Date(fromDate).toISOString());
      if (toDate) {
        const end = new Date(toDate);
        end.setHours(23, 59, 59, 999);
        params.set('to', end.toISOString());
      }
      const r = await fetch(`/api/audit-events?${params.toString()}`, { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setEvents(data.events ?? []);
      setTotal(data.total ?? 0);
      setActions(data.knownActions ?? []);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); /* eslint-disable-next-line */ }, [page, actionFilter, fromDate, toDate]);

  const filtered = events.filter((e) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      e.action.toLowerCase().includes(q)
      || e.actor?.name.toLowerCase().includes(q)
      || e.targetType?.toLowerCase().includes(q)
      || e.targetId?.toLowerCase().includes(q)
      || e.reason?.toLowerCase().includes(q)
    );
  });

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminLayout title="Audit log" subtitle={`Every privileged action — ${total} events`}>
      {error && (
        <div className="rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2 mb-5">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <section className="rounded-lg bg-surface border border-border p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Quick search (in-page)</label>
            <div className="relative">
              <IconSearch size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="action, actor, target, reason"
                className="w-full h-10 pl-9 pr-3 rounded-md bg-light border border-border focus:border-primary outline-none text-13 font-semibold text-heading"
              />
            </div>
          </div>
          <div>
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Action</label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="h-10 px-3 rounded-md bg-light border border-border outline-none text-13 font-semibold text-heading min-w-[180px]"
            >
              <option value="">All actions</option>
              {actions.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              className="h-10 px-3 rounded-md bg-light border border-border outline-none text-13 font-semibold text-heading"
            />
          </div>
          <div>
            <label className="block text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              className="h-10 px-3 rounded-md bg-light border border-border outline-none text-13 font-semibold text-heading"
            />
          </div>
        </div>
      </section>

      <section className="rounded-lg bg-surface border border-border p-4">
        {loading ? (
          <p className="text-13 italic text-gray-700">Loading…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 rounded-2xl bg-light text-gray-700 inline-flex items-center justify-center mb-3">
              <IconShieldHalf size={22} stroke={2} />
            </div>
            <p className="text-14 font-bold text-heading">No events</p>
            <p className="text-13 font-medium text-gray-700 mt-1">No audit events match the current filters.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-13">
              <thead>
                <tr className="text-left text-12 font-bold uppercase tracking-wider text-gray-700">
                  <th className="py-2 pr-3">When</th>
                  <th className="py-2 pr-3">Action</th>
                  <th className="py-2 pr-3">Actor</th>
                  <th className="py-2 pr-3">Target</th>
                  <th className="py-2 pr-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e) => (
                  <tr
                    key={e.id}
                    onClick={() => setOpenEvent(e)}
                    className="border-t border-border-soft cursor-pointer hover:bg-light"
                  >
                    <td className="py-2.5 pr-3 text-gray-700 tabular-nums whitespace-nowrap">{fmtDateTime(e.ts)}</td>
                    <td className="py-2.5 pr-3">
                      <span className={`inline-flex items-center px-2 h-6 rounded-md text-11 font-bold ${ACTION_TONE[e.action] ?? 'bg-light text-gray-700'}`}>
                        {e.action}
                      </span>
                    </td>
                    <td className="py-2.5 pr-3">
                      <p className="font-semibold text-heading">{e.actor?.name ?? '—'}</p>
                      {e.actorRole && <p className="text-11 font-medium text-gray-700 uppercase">{e.actorRole}</p>}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700">
                      {e.targetType && <span className="text-12 font-mono">{e.targetType}</span>}
                      {e.targetId && <span className="text-11 font-mono opacity-70 ml-1">{e.targetId.slice(0, 8)}…</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-gray-700 italic truncate max-w-xs">{e.reason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-border">
          <p className="text-12 font-medium text-gray-700">
            Showing {filtered.length === 0 ? 0 : (page - 1) * pageSize + 1}–{(page - 1) * pageSize + filtered.length} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1 || loading} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100 disabled:opacity-40">
              <IconChevronLeft size={14} stroke={2.5} /> Prev
            </button>
            <span className="text-13 font-semibold text-heading tabular-nums">Page {page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages || loading} className="h-9 px-3 inline-flex items-center gap-1.5 rounded-md bg-light border border-border text-13 font-bold text-heading hover:bg-gray-100 disabled:opacity-40">
              Next <IconChevronRight size={14} stroke={2.5} />
            </button>
          </div>
        </div>
      </section>

      {/* Detail modal */}
      <Modal open={openEvent !== null} onClose={() => setOpenEvent(null)} size="lg" title={openEvent ? openEvent.action : ''}>
        {openEvent && (
          <div className="space-y-3 text-13">
            <Row label="When" value={fmtDateTime(openEvent.ts)} mono />
            <Row label="Actor" value={openEvent.actor?.name ?? '—'} sub={openEvent.actorRole ?? undefined} />
            <Row label="Target" value={openEvent.targetType ?? '—'} sub={openEvent.targetId ?? undefined} mono />
            {openEvent.reason && <Row label="Reason" value={openEvent.reason} />}
            {openEvent.ip && <Row label="IP" value={openEvent.ip} mono />}
            {openEvent.before !== null && openEvent.before !== undefined && (
              <div>
                <p className="text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">Before</p>
                <pre className="font-mono text-12 bg-light border border-border-soft rounded-md p-3 overflow-x-auto">{JSON.stringify(openEvent.before, null, 2)}</pre>
              </div>
            )}
            {openEvent.after !== null && openEvent.after !== undefined && (
              <div>
                <p className="text-12 font-bold uppercase tracking-wider text-gray-700 mb-1.5">After</p>
                <pre className="font-mono text-12 bg-light border border-border-soft rounded-md p-3 overflow-x-auto">{JSON.stringify(openEvent.after, null, 2)}</pre>
              </div>
            )}
          </div>
        )}
      </Modal>
    </AdminLayout>
  );
}

function Row({ label, value, sub, mono }: { label: string; value: string; sub?: string; mono?: boolean }) {
  return (
    <div className="flex gap-3 p-2.5 rounded-md bg-light border border-border-soft">
      <p className="text-12 font-bold uppercase tracking-wider text-gray-700 w-20 flex-shrink-0">{label}</p>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-heading ${mono ? 'font-mono text-12' : 'text-14'}`}>{value}</p>
        {sub && <p className="text-12 text-gray-700 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}
